import { useCallback, type ChangeEvent, type ReactNode } from "react";
import { Checkbox, Group, Stack, TextInput } from "@mantine/core";

interface CaseImageReportControlsProps {
  imageId: string;
  caption: string;
  includeInReport: boolean;
  readOnly: boolean;
  includeReadOnly?: boolean;
  onCaptionChange: (imageId: string, caption: string) => void;
  onIncludeInReportChange: (imageId: string, include: boolean) => void;
  actions?: ReactNode;
}

export function CaseImageReportControls({
  imageId,
  caption,
  includeInReport,
  readOnly,
  includeReadOnly,
  onCaptionChange,
  onIncludeInReportChange,
  actions,
}: CaseImageReportControlsProps) {
  const handleCaptionChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onCaptionChange(imageId, event.currentTarget.value);
    },
    [imageId, onCaptionChange]
  );
  const handleIncludeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onIncludeInReportChange(imageId, event.currentTarget.checked);
    },
    [imageId, onIncludeInReportChange]
  );

  return (
    <Stack gap={6}>
      <TextInput
        label="Caption (optional)"
        description="Appears below this photo in the report."
        value={caption}
        onChange={handleCaptionChange}
        disabled={readOnly}
        maxLength={100}
        styles={{ input: { minHeight: 44 } }}
      />
      <Group gap={4} justify="space-between" wrap="nowrap">
        <Checkbox
          size="xs"
          label="Add to report"
          checked={includeInReport}
          disabled={includeReadOnly ?? readOnly}
          onChange={handleIncludeChange}
          styles={{ label: { cursor: "pointer", fontSize: 11 } }}
        />
        {actions}
      </Group>
    </Stack>
  );
}
